import React, { useEffect, useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import * as faceapi from '@vladmandic/face-api';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';

import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import {
  collection,
  addDoc,
  setDoc,
  doc,
  getDocs,
  getDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';

import 'react-toastify/dist/ReactToastify.css';
import '../Style/ClockIn.css';

/* ───────── helpers ───────── */
const distance = (d1, d2) =>
  Math.sqrt(d1.reduce((acc, _, i) => acc + (d1[i] - d2[i]) ** 2, 0));

const startOfDay = (d = new Date()) => {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return s;
};

const MATCH_THRESHOLD = 0.45; // 0.55–0.60 if too strict

export default function ClockIn() {
  const videoRef = useRef(null);
  const auth = getAuth();

  // Toast styling
  const toastCfg = {
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    className: 'clock-toast',
    bodyClassName: 'clock-toast-body',
  };
  const notify = {
    success: (m) => toast.success(m, toastCfg),
    error:   (m) => toast.error(m, toastCfg),
    info:    (m) => toast.info(m, toastCfg),
    warn:    (m) => toast.warn(m, toastCfg),
  };

  // UI state
  const [loadingModels, setLoadingModels] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [clockType, setClockType] = useState(() => localStorage.getItem('clockType') || 'in');
  const [registerMode, setRegisterMode] = useState(false);

  // Data
  const [users, setUsers] = useState([]); // users docs
  const [usersById, setUsersById] = useState({});
  const [usersByAuth, setUsersByAuth] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(''); // users doc id (admin selection)
  const [knownFaces, setKnownFaces] = useState([]); // [{id, descriptor(F32), firstName?, lastName?, meta?}]
  const [isAdmin, setIsAdmin] = useState(false);

  // Success banner
  const [matchBanner, setMatchBanner] = useState(null); // {name, type, when}

  /* ── model loader ───────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        try {
          await faceapi.tf.setBackend('webgl');
          await faceapi.tf.ready();
        } catch {
          await faceapi.tf.setBackend('cpu');
          await faceapi.tf.ready();
        }
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
        ]);
        setLoadingModels(false);
        notify.success('Face-API models loaded');
      } catch (err) {
        console.error(err);
        notify.error('Unable to load face-recognition models');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── users + faces ──────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        // users
        const snap = await getDocs(collection(db, 'users'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(list);

        // build quick maps
        const mapById = {};
        const mapByAuth = {};
        list.forEach((u) => {
          mapById[u.id] = u;
          if (u.authId) mapByAuth[u.authId] = u;
        });
        setUsersById(mapById);
        setUsersByAuth(mapByAuth);

        // role
        const me =
          mapByAuth[auth.currentUser?.uid || ''] ||
          mapById[auth.currentUser?.uid || ''];
        setIsAdmin(me?.role === 'Admin');

        // faces
        const faceSnap = await getDocs(collection(db, 'registeredFaces'));
        const faces = faceSnap.docs.map((d) => {
          const data = d.data() || {};
          const arr = data.descriptor || [];
          const f32 = arr instanceof Float32Array ? arr : new Float32Array(arr);

          // Try to join to a user using: face doc id (authId), or metadata, or users doc id
          const userFromId   = mapById[d.id];          // if you used users doc id once
          const userFromAuth = mapByAuth[d.id];        // if face doc id == authId (our target)
          const userFromMeta =
            (data.userId && mapById[data.userId]) ||
            (data.authId && mapByAuth[data.authId]);

          const user = userFromAuth || userFromMeta || userFromId || {};

          return {
            id: d.id, // registeredFaces doc id (ideally authId)
            descriptor: f32,
            firstName: user.firstName || data.firstName || '',
            lastName:  user.lastName  || data.lastName  || '',
            meta: {
              userId: user.id || data.userId || '',
              authId: user.authId || data.authId || '',
              email:  user.email || data.email || '',
            }
          };
        });
        setKnownFaces(faces);
      } catch (err) {
        console.error('Failed to load users/faces:', err);
        notify.error('Failed to load users/faces — check console');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser]);

  useEffect(() => {
    localStorage.setItem('clockType', clockType);
  }, [clockType]);

  const descriptorExists = useCallback(
    (desc) => knownFaces.some((f) => distance(f.descriptor, desc) < MATCH_THRESHOLD),
    [knownFaces]
  );

  const onUserMediaError = (e) => {
    console.error('Camera error:', e?.message || e);
    notify.error('Camera blocked or not available. Allow camera access and try again.');
  };

  /* ---------------- KEY: robust resolver (NO user prompt) ---------------- */
  const resolveIdentity = useCallback(
    async (faceDocId) => {
      // 1) Face ID equals authId (our preferred scheme)
      let u = usersByAuth[faceDocId];
      if (u) {
        return {
          userId: u.id,
          authId: u.authId || u.id,
          email:  u.email || '',
          firstName: u.firstName || '',
          lastName:  u.lastName  || '',
          certainty: 'byAuthId',
        };
      }

      // 2) Face ID equals users doc id (older installs)
      u = usersById[faceDocId];
      if (u) {
        return {
          userId: u.id,
          authId: u.authId || u.id,
          email:  u.email || '',
          firstName: u.firstName || '',
          lastName:  u.lastName  || '',
          certainty: 'byUsersDocId',
        };
      }

      // 3) Check metadata stored on the face doc (if any)
      try {
        const rf = await getDoc(doc(db, 'registeredFaces', faceDocId));
        if (rf.exists()) {
          const m = rf.data() || {};
          // try to follow metadata back to a users doc
          const fromMetaUser =
            (m.userId && (usersById[m.userId] || (await getDoc(doc(db, 'users', m.userId))).data())) ||
            (m.authId && usersByAuth[m.authId]);

          if (fromMetaUser && fromMetaUser.id) {
            const resolved = usersById[fromMetaUser.id] || fromMetaUser;
            return {
              userId: resolved.id,
              authId: resolved.authId || resolved.id,
              email:  resolved.email || m.email || '',
              firstName: resolved.firstName || m.firstName || '',
              lastName:  resolved.lastName  || m.lastName  || '',
              certainty: 'byFaceMetaLinked',
            };
          }

          // If metadata includes names/emails, use them directly (last resort)
          if (m.firstName || m.lastName || m.email) {
            return {
              userId: m.userId || faceDocId,          // store something stable
              authId: m.authId || m.userId || faceDocId,
              email:  m.email || '',
              firstName: m.firstName || '',
              lastName:  m.lastName  || '',
              certainty: 'byFaceMetaNames',
            };
          }
        }
      } catch (e) {
        console.warn('resolve by face meta failed', e);
      }

      // 4) Couldn’t resolve safely — do NOT guess
      return null;
    },
    [usersById, usersByAuth]
  );

  /* ---------------- registration: make face docId = authId ---------------- */
  const registerFaceForUser = useCallback(
    async (userDocId, descriptorF32) => {
      const person =
        usersById[userDocId] ||
        (await (async () => {
          const s = await getDoc(doc(db, 'users', userDocId));
          return s.exists() ? { id: s.id, ...s.data() } : null;
        })());

      if (!person) {
        notify.error('Selected user not found');
        return false;
      }

      // Use AUTH ID for the face doc id
      const faceDocId = person.authId || person.id;

      // Clean any old face doc stored under users doc id
      if (faceDocId !== person.id) {
        const oldRef = await getDoc(doc(db, 'registeredFaces', person.id));
        if (oldRef.exists()) {
          try { await deleteDoc(doc(db, 'registeredFaces', person.id)); } catch { /* ignore */ }
        }
      }

      // Save descriptor + metadata under /registeredFaces/{authId}
      await setDoc(doc(db, 'registeredFaces', faceDocId), {
        descriptor: Array.from(descriptorF32),
        userId: person.id,
        authId: person.authId || person.id,
        firstName: person.firstName || '',
        lastName:  person.lastName  || '',
        email:     person.email     || '',
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Update local cache
      setKnownFaces((prev) => {
        const filtered = prev.filter((f) => f.id !== faceDocId && f.meta?.userId !== person.id);
        return [
          ...filtered,
          {
            id: faceDocId,
            descriptor: descriptorF32,
            firstName: person.firstName || '',
            lastName:  person.lastName  || '',
            meta: { userId: person.id, authId: person.authId || person.id, email: person.email || '' },
          },
        ];
      });

      notify.success(`Saved face for ${person.firstName || ''} ${person.lastName || ''}`);
      return true;
    },
    [usersById, notify]
  );

  /* ---------------- attendance writer (ALTERNATING LOGIC) ---------------- */
  const writeAttendance = useCallback(async (identity, type) => {
    const { userId, authId, email, firstName, lastName } = identity;

    const attCol = collection(db, 'attendance');
    const sod = startOfDay(new Date());

    // get the most recent attendance record for this user today
    const lastQ = query(
      attCol,
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(sod)),
      orderBy('date', 'desc'),
      limit(1)
    );
    const lastSnap = await getDocs(lastQ);
    const last = lastSnap.empty ? null : lastSnap.docs[0].data();

    // RULES:
    // - First action of the day must be IN.
    // - Subsequent actions must ALTERNATE: IN -> OUT -> IN -> OUT ...
    if (!last) {
      if (type === 'out') {
        notify.warn('No clock-in found for today. Please clock in first.');
        return false;
      }
    } else {
      if (last.type === type) {
        if (type === 'in') {
          notify.info('You are already clocked in. Please clock out before clocking in again.');
        } else {
          notify.info('You are already clocked out. Please clock in before clocking out again.');
        }
        return false;
      }
    }

    // If clocking OUT, optionally compute session duration from last IN
    let extra = {};
    if (type === 'out') {
      // last should be an "in" (because of alternation rule),
      // but be defensive and fetch the last "in" just in case.
      let lastInDate = null;
      if (last?.type === 'in' && last?.date) {
        lastInDate = last.date.toDate ? last.date.toDate() : new Date(last.date);
      } else {
        const lastInQ = query(
          attCol,
          where('userId', '==', userId),
          where('type', '==', 'in'),
          where('date', '>=', Timestamp.fromDate(sod)),
          orderBy('date', 'desc'),
          limit(1)
        );
        const lastInSnap = await getDocs(lastInQ);
        if (!lastInSnap.empty) {
          const d = lastInSnap.docs[0].data();
          lastInDate = d.date?.toDate ? d.date.toDate() : new Date(d.date);
        }
      }
      if (lastInDate) {
        const ms = Date.now() - lastInDate.getTime();
        extra.sessionMs = ms;
        extra.sessionHours = Math.round((ms / 36e5) * 100) / 100;
      }
    }

    await addDoc(attCol, {
      userId,                 // users doc id (stable for joins)
      authId,                 // auth UID
      email,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      date: Timestamp.now(),
      type,
      ...extra,
    });

    const fullName = `${firstName} ${lastName}`.trim() || 'Employee';
    setMatchBanner({ name: fullName, type, when: new Date() });
    notify.success(type === 'in' ? `Welcome ${fullName} (clocked in)` : `Bye ${fullName} (clocked out)`);
    setTimeout(() => setMatchBanner(null), 5000);
    return true;
  }, [notify]);

  /* ---------------- main scan / register ---------------- */
  const handleScan = async () => {
    if (loadingModels) return notify.info('Models still loading…');
    setScanning(true);
    setMatchBanner(null);

    try {
      const webcam = videoRef.current;
      if (!webcam) return notify.error('Camera not ready');

      const shot = webcam.getScreenshot();
      if (!shot) return notify.error('No camera image — allow camera access and try again');

      const img = await faceapi.fetchImage(shot);
      const res = await faceapi
        .detectSingleFace(img)
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!res) return notify.error('Face not detected — center your face and try again');

      const currentDesc = new Float32Array(Array.from(res.descriptor));

      /* ---- registration (admin only) ---- */
      if (registerMode) {
        if (!selectedUserId) return notify.warn('Choose an employee first');
        // prevent duplicates
        if (descriptorExists(currentDesc)) {
          return notify.error('This face is already registered');
        }
        const ok = await registerFaceForUser(selectedUserId, currentDesc);
        if (ok) setSelectedUserId('');
        return;
      }

      /* ---- recognition ---- */
      if (!knownFaces.length) {
        return notify.warn('No registered faces yet — ask an admin to register you');
      }

      let best = null;
      let bestDist = Number.POSITIVE_INFINITY;
      knownFaces.forEach((f) => {
        const d = distance(f.descriptor, currentDesc);
        if (d < bestDist) { bestDist = d; best = f; }
      });

      if (!best || bestDist > MATCH_THRESHOLD) {
        return notify.error('Face not recognised — please register with admin');
      }

      // Resolve identity WITHOUT asking the user
      const idRes = await resolveIdentity(best.id);
      if (!idRes) {
        notify.error(
          'This face is not linked to any user yet. Please ask an admin to re-register your face (it will be saved under your Auth ID).'
        );
        return;
      }

      await writeAttendance(idRes, clockType);
    } catch (e) {
      console.error(e);
      notify.error('Unexpected error — check console');
    } finally {
      setScanning(false);
    }
  };

  /* ── UI ──────────────────────────────────────────────────────── */
  return (
    <div className="clockin-page">
      <header className="title-wrap">
        <h2 className="title">Employee Clock-In / Clock-Out</h2>
        <p className="subtitle">Good light • Face camera • Keep head &amp; shoulders in frame</p>
      </header>

      {/* camera */}
      <motion.div
        className={`cam-wrapper ${scanning ? 'scanning' : ''} ${clockType}`}
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Webcam
          ref={videoRef}
          className="webcam-view"
          mirrored
          screenshotFormat="image/jpeg"
          screenshotQuality={0.92}
          onUserMediaError={onUserMediaError}
          videoConstraints={{
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          }}
        />
        {scanning && (
          <div className="overlay">
            <ClipLoader size={80} speedMultiplier={1.3} />
          </div>
        )}
      </motion.div>

      {/* in / out pills */}
      <motion.div className="mode-toggle" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {['in', 'out'].map((t) => (
          <motion.label
            key={t}
            className={`pill ${t} ${clockType === t ? 'active' : ''}`}
            whileTap={{ scale: 0.94 }}
          >
            <input
              type="radio"
              name="clockType"
              value={t}
              checked={clockType === t}
              onChange={() => setClockType(t)}
            />
            {t === 'in' ? 'Clock In' : 'Clock Out'}
          </motion.label>
        ))}
      </motion.div>

      {/* buttons */}
      <div className="controls">
        <motion.button
          className={`btn scan ${clockType}`}
          onClick={handleScan}
          disabled={scanning || loadingModels}
          whileTap={{ scale: 0.97 }}
        >
          {loadingModels ? 'Loading models…' : scanning ? 'Scanning…' : 'Scan Face'}
        </motion.button>

        {isAdmin && (
          <motion.button
            className="btn toggle"
            onClick={() => { setRegisterMode(!registerMode); toast.dismiss(); }}
            whileTap={{ scale: 0.97 }}
          >
            {registerMode ? '← Back' : 'Register New Face'}
          </motion.button>
        )}
      </div>

      {/* admin-only picker */}
      {registerMode && isAdmin && (
        <motion.select
          className="user-select"
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <option value="">— Select employee —</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.firstName} {u.lastName} {u.role ? `(${u.role})` : ''}
            </option>
          ))}
        </motion.select>
      )}

      {/* prominent match name banner */}
      {matchBanner && (
        <div className={`match-banner ${matchBanner.type}`}>
          <div className="mb-line">
            <span className="mb-title">
              {matchBanner.type === 'in' ? 'Clocked In:' : 'Clocked Out:'}
            </span>
            <span className="mb-name">{matchBanner.name}</span>
          </div>
          <div className="mb-time">
            {matchBanner.when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
}
